import { memo } from 'react';
import PropTypes from 'prop-types';
import {
    IconAudiocall,
    IconVideocall,
    IconIncoming,
    IconOutgoing,
    IconIncomingMissed,
    GroupAvatarIcon,
} from '../../../assets/images';
import { CustomButton } from '../../calls/ui/components/Common/UI';
import { UserProfile } from '../../calls/ui/components/Common';
import { useLanguage } from '../../../localization/useLanguage';

import GroupAvatar from './GroupAvatar';

const getFormattedNames = participants => {
    if (!participants || participants.length === 0) return '';
    const names = participants.map(p => p.name);
    if (names.length > 3) {
        return `${names.slice(0, 3).join(', ')} ...`;
    }
    return names.join(', ');
};

const CallLogItem = ({ log, isSelectionMode, isSelected, onToggleSelection, onClick, isActive, onCallBack }) => {
    const { t } = useLanguage();
    const handleItemClick = () => {
        if (isSelectionMode) {
            onToggleSelection(log.id);
        } else if (onClick) {
            onClick(log);
        }
    };

    const renderCallDirectionIcon = () => {
        if (log.isMissed && log.isIncoming) return <IconIncomingMissed />;
        if (log.isIncoming) return <IconIncoming />;
        return <IconOutgoing />;
    };

    const fullName = log.groupName || getFormattedNames(log.participants);

    return (
        <CustomButton
            as="div"
            className={`callScreenItem ct-flex ct-aiCenter ct-jcBetween ${isSelectionMode ? 'call-item--select-mode' : ''} ${isSelected ? 'selected' : ''} ${isActive ? 'active' : ''}`}
            onClick={handleItemClick}
        >
            <div className="callScreenItemLeft ct-flex ct-aiCenter ct-gap16 ct-relative">
                {isSelectionMode && (
                    <div className="callScreenItemCheckbox abs-checkbox ct-flexCenter ct-cursor">
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                                // Handled by the parent onClick wrapper
                            }}
                            onClick={e => {
                                // Prevent double-firing from the wrapper div
                                e.stopPropagation();
                                handleItemClick();
                            }}
                            aria-label={`Select item`}
                        />
                    </div>
                )}

                <div className="callScreenAvatarContainer ct-flexCenter ct-relative">
                    {log.groupName || log.participants.length === 1 ? (
                        <UserProfile
                            userId={log.groupId ? log.groupId : log.participants[0].userId}
                            className="chat-avatar chat-avatar-fallback ct-flexCenter"
                            defaultIcon={log.groupName ? <GroupAvatarIcon /> : null}
                        />
                    ) : (
                        <GroupAvatar participants={log.participants} />
                    )}
                </div>

                <div className="callScreenInfo ct-flex ct-fcol ct-jcCenter">
                    <span
                        className={`callScreenName ct-ellipsis ${log.isMissed && log.isIncoming ? 'callScreenNameMissed' : ''}`}
                        title={fullName}
                    >
                        {fullName.length > 25 ? `${fullName.substring(0, 25)}...` : fullName}
                    </span>
                    <div className="callScreenDetails ct-flex ct-aiCenter ct-gap6">
                        <span className="callScreenTypeIcon ct-flexCenter">
                            <i className="ct-icon xs-sm">{renderCallDirectionIcon()}</i>
                        </span>
                        <span className="callScreenDatetime">{log.dateStr}</span>
                    </div>
                </div>
            </div>

            <div className="callScreenItemRight ct-flex ct-aiCenter ct-gap12">
                {log.duration && <span className="callScreenDuration">{log.duration}</span>}
                <button
                    className="callScreenActionBtn ct-flexCenter ct-cursor"
                    aria-label={log.isVideo ? t('calls.LOGS.VIDEO_CALL') : t('calls.LOGS.VOICE_CALL')}
                    title={log.isVideo ? t('calls.LOGS.VIDEO_CALL') : t('calls.LOGS.VOICE_CALL')}
                    onClick={e => {
                        e.stopPropagation();
                        if (onCallBack) onCallBack(log);
                    }}
                >
                    {log.isVideo ? (
                        <i className="ct-icon ct-xl">
                            <IconVideocall />
                        </i>
                    ) : (
                        <i className="ct-icon ct-xl">
                            <IconAudiocall />
                        </i>
                    )}
                </button>
            </div>
        </CustomButton>
    );
};

CallLogItem.propTypes = {
    log: PropTypes.shape({
        id: PropTypes.string,
        groupName: PropTypes.string,
        groupId: PropTypes.string,
        participants: PropTypes.arrayOf(
            PropTypes.shape({
                userId: PropTypes.string,
                name: PropTypes.string,
                avatarUrl: PropTypes.string,
            }),
        ),
        isMissed: PropTypes.bool,
        isIncoming: PropTypes.bool,
        isVideo: PropTypes.bool,
        dateStr: PropTypes.string,
        duration: PropTypes.string,
    }),
    isSelectionMode: PropTypes.bool,
    isSelected: PropTypes.bool,
    onToggleSelection: PropTypes.func,
    onClick: PropTypes.func,
    onCallBack: PropTypes.func,
    isActive: PropTypes.bool,
};

export default memo(CallLogItem);
